/**
 * Multi-series trend line over a shared categorical x-axis.
 * Geometry ported from block ① of docs/plans/conversations/09-dashboard-mockup.html.
 *
 * What is load-bearing here:
 *  - A HOLE IS NOT A ZERO: a `null` point closes the current subpath; the line is
 *    broken, never bridged, never drawn at 0.
 *  - Direct end labels (no legend hunting) with LEADER LINES when they collide:
 *    ends sorted by y, nudged forward with MINGAP, clamped backward off the bottom,
 *    then a thin connector at 0.55 opacity from the label to the line end. Since
 *    2026-10-07 each one is a PILL (mock `.tag`): ink-filled and label-bearing when
 *    there is one series ("Score 16"), tinted in its own hue and value-only when
 *    there are several — the legend names those, so repeating the name in every
 *    pill just makes them collide.
 *  - `area` fills under the line (mock: lavender under violet). Only meaningful for
 *    a single series — stacked translucent areas stop being readable at two.
 *  - `stacked` is the other answer to several series that ADD UP (citation counts by
 *    source): each layer is drawn on top of the ones before it as an OPAQUE band, so
 *    the top edge is the total. A layer `null` at an x adds nothing there and the
 *    tooltip says it was not measured; an x where EVERY layer is `null` is still a
 *    hole. One end pill, for the total (`totalLabel`).
 *  - Event markers: 1px vertical rule + small downward flag + label in a reserved
 *    top band, nudged horizontally, clamped at the right edge, each with an 18px
 *    invisible hover hit rect. Never a series colour (an event is not a measure).
 *  - One axis. Crosshair + tooltip listing every series' value at that x.
 *
 * x labels arrive already bucketed by the caller (weekly rollup etc.).
 */

import { useRef, useState } from 'react';
import { Box } from '@mui/material';
import { VIZ, fmtPct, measureText, niceTicks, shade, tint, useMeasuredWidth } from './tokens';
import { EmptyNote, VizLegend } from './ChartCard';
import { useVizTooltip } from './useVizTooltip';

export interface TrendLineSeries {
  key: string;
  label: string;
  color: string;
  points: (number | null)[];
  /**
   * Stroke width, default 2. Only reason to raise it: one series is an AGGREGATE of
   * the others (an average), which is not a category — so it is set apart by weight
   * and an ink colour rather than by being handed a categorical hue it would then
   * steal from a real series.
   */
  width?: number;
  /** Dashed stroke: a second reading of the same measure on its own scale (the Image
   *  per-criterion score beside the alignment score, F step 8b). */
  dashed?: boolean;
}

export interface TrendLineProps {
  labels: string[];
  series: TrendLineSeries[];
  /** `dotted`: a dotted rule (a method boundary, not a dated event). */
  events?: { index: number; label: string; dotted?: boolean }[];
  format?: (v: number) => string;
  yMax?: number;
  height?: number;
  emptyNote?: string;
  unmeasuredNote?: string;
  /** Fill under the line. Single-series only — see the header note. */
  area?: boolean;
  /** Stack the series (they must add up) — see the header note. */
  stacked?: boolean;
  /** Stacked only: the end pill's and the tooltip's name for the top edge. */
  totalLabel?: string;
}

/**
 * Running totals, bottom layer first: `out[j][i]` = Σ layers 0..j at `i`, a `null`
 * layer adding nothing. `null` only where EVERY layer is `null` — a hole stays a hole.
 */
export function stackLayers(layers: (number | null)[][], n: number): (number | null)[][] {
  let run: (number | null)[] = Array.from({ length: n }, (_, i) =>
    layers.some((pts) => pts[i] != null) ? 0 : null,
  );
  return layers.map((pts) => {
    run = run.map((r, i) => (r == null ? null : r + (pts[i] ?? 0)));
    return run;
  });
}

const MINGAP = 18;
const BAND = 24; // reserved top band for the event labels
/** Pill geometry (mock `.tag`): 4px/9px padding on a 12px label. */
const PILL_PAD_X = 9;
const PILL_H = 22;

export default function TrendLine({
  labels,
  series,
  events = [],
  format = fmtPct,
  yMax,
  height = 316,
  emptyNote = 'Aucune donnée sur cette sélection.',
  unmeasuredNote = 'aucune mesure',
  area = false,
  stacked = false,
  totalLabel = 'Total',
}: TrendLineProps) {
  const [ref, width] = useMeasuredWidth<HTMLDivElement>(900);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tip = useVizTooltip();
  const [crossI, setCross] = useState<number | null>(null);

  const np = labels.length;
  const hasPoint = series.some((s) => s.points.some((p) => p != null));
  if (!np || !series.length || !hasPoint) return <EmptyNote>{emptyNote}</EmptyNote>;

  /**
   * What is DRAWN: the series as given, or — stacked — each one's running total from
   * the bottom layer up. `null` only where no layer at all was measured.
   */
  const drawn = stacked ? stackLayers(series.map((s) => s.points), np) : series.map((s) => s.points);
  const totals = stacked ? drawn[drawn.length - 1] : null;

  // --- end labels first: they set the right margin, so nothing is ever clipped ---
  const lastIdx = (vals: (number | null)[]) => {
    for (let i = vals.length - 1; i >= 0; i--) if (vals[i] != null) return i;
    return null;
  };
  // One series ⇒ the pill names the measure; several ⇒ the legend does, and the
  // pills carry values only so they fit side by side.
  const named = series.length === 1 || stacked;
  const endTexts = series.map((s, j) => {
    if (totals) {
      if (j !== series.length - 1) return '';
      const i = lastIdx(totals);
      return i == null ? '' : `${totalLabel} ${format(totals[i] as number)}`;
    }
    const i = lastIdx(s.points);
    if (i == null) return '';
    const v = format(s.points[i] as number);
    return named ? `${s.label} ${v}` : v;
  });
  const endW = Math.max(0, ...endTexts.map((t) => (t ? measureText(t, 12, 600) + 2 * PILL_PAD_X : 0)));

  const W = Math.max(360, width);
  const H = height;
  const m = { t: events.length ? 14 + BAND : 16, r: endW ? endW + 20 : 12, b: 28, l: 48 };
  const pw = Math.max(40, W - m.l - m.r);
  const ph = Math.max(60, H - m.t - m.b);

  const all = drawn.flat().filter((v): v is number => v != null);
  // `yMax` is a floor: rounded up to the next nice step so the top gridline is the
  // axis top. TWO intervals ⇒ three rules (0 / 50 / 100), the mock's density: a
  // 6-line grid competes with the line it is there to support.
  const ticks = niceTicks(Math.max(yMax ?? 0, 0.0001, ...all), 2);
  const max = ticks[ticks.length - 1] || 1;
  const X = (i: number) => m.l + (i / Math.max(1, np - 1)) * pw;
  const Y = (v: number) => m.t + ph - (v / max) * ph;

  // --- null-breaking subpaths -------------------------------------------------
  const pathOf = (points: (number | null)[]) => {
    let d = '';
    let open = false;
    points.forEach((v, i) => {
      if (v == null) {
        open = false;
        return;
      }
      d += `${open ? 'L' : 'M'}${X(i).toFixed(1)},${Y(v).toFixed(1)}`;
      open = true;
    });
    return d;
  };

  /**
   * One closed shape PER CONTIGUOUS RUN, dropped to the baseline at each end — so a
   * gap in the series is a gap in the fill too. One shape spanning the holes would
   * paint area under days that were never measured.
   */
  const areaOf = (points: (number | null)[]) => {
    const base = (m.t + ph).toFixed(1);
    let d = '';
    let run: number[] = [];
    const flush = () => {
      if (run.length) {
        d += `M${X(run[0]).toFixed(1)},${base}`;
        for (const i of run) d += `L${X(i).toFixed(1)},${Y(points[i] as number).toFixed(1)}`;
        d += `L${X(run[run.length - 1]).toFixed(1)},${base}Z`;
      }
      run = [];
    };
    points.forEach((v, i) => (v == null ? flush() : run.push(i)));
    flush();
    return d;
  };

  /**
   * Stacked band of layer `j`: from the layer below (or the baseline) up to its own
   * running total, one closed shape per contiguous run — same hole rule as `areaOf`.
   */
  const bandOf = (j: number) => {
    const upper = drawn[j];
    const lower = j > 0 ? drawn[j - 1] : null;
    const yLow = (i: number) => (lower ? Y(lower[i] as number) : m.t + ph);
    let d = '';
    let run: number[] = [];
    const flush = () => {
      if (run.length) {
        run.forEach((i, k) => (d += `${k ? 'L' : 'M'}${X(i).toFixed(1)},${Y(upper[i] as number).toFixed(1)}`));
        for (const i of [...run].reverse()) d += `L${X(i).toFixed(1)},${yLow(i).toFixed(1)}`;
        d += 'Z';
      }
      run = [];
    };
    upper.forEach((v, i) => (v == null ? flush() : run.push(i)));
    flush();
    return d;
  };

  // --- end-label layout: nudge forward, clamp backward, then leader lines -----
  type End = { px: number; py: number; ly: number; color: string; text: string };
  const ends: End[] = [];
  series.forEach((s, j) => {
    if (!endTexts[j]) return;
    const i = lastIdx(drawn[j]);
    if (i == null) return;
    ends.push({ px: X(i), py: Y(drawn[j][i] as number), ly: 0, color: s.color, text: endTexts[j] });
  });
  ends.sort((a, b) => a.py - b.py);
  ends.forEach((e, i) => {
    e.ly = i ? Math.max(e.py, ends[i - 1].ly + MINGAP) : e.py;
  });
  for (let i = ends.length - 1; i > 0; i--) {
    if (ends[i].ly > m.t + ph) ends[i].ly = m.t + ph;
    if (ends[i].ly - ends[i - 1].ly < MINGAP) ends[i - 1].ly = ends[i].ly - MINGAP;
  }
  const labelX = m.l + pw + 12;

  // --- event label layout: horizontal nudging + right-edge clamp --------------
  const evs = events
    .filter((e) => e.index >= 0 && e.index < np)
    .map((e) => ({ ...e, x: X(e.index), w: measureText(e.label, 11), lx: 0 }))
    .sort((a, b) => a.x - b.x);
  evs.forEach((e, i) => {
    e.lx = i ? Math.max(e.x, evs[i - 1].lx + evs[i - 1].w + 12) : e.x;
    if (e.lx + e.w > m.l + pw) e.lx = m.l + pw - e.w;
  });

  const xTicks = [0, 1, 2, 3]
    .map((j) => Math.round((j / 3) * (np - 1)))
    .filter((v, i, a) => a.indexOf(v) === i);

  const onMove = (ev: React.PointerEvent<SVGRectElement>) => {
    const box = svgRef.current?.getBoundingClientRect();
    if (!box) return;
    const px = ((ev.clientX - box.left) / box.width) * W;
    const i = Math.max(0, Math.min(np - 1, Math.round(((px - m.l) / pw) * (np - 1))));
    setCross(i);
    tip.show(ev, [
      labels[i],
      ...series.map((s) => `${s.label} : ${s.points[i] == null ? unmeasuredNote : format(s.points[i] as number)}`),
      ...(totals && totals[i] != null ? [`${totalLabel} : ${format(totals[i] as number)}`] : []),
      ...evs.filter((e) => e.index === i).map((e) => e.label),
    ]);
  };

  return (
    <Box ref={ref} sx={{ width: '100%' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        style={{ display: 'block', overflow: 'visible' }}
        role="img"
        aria-label={`Évolution de ${series.map((s) => s.label).join(', ')}`}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line x1={m.l} x2={m.l + pw} y1={Y(t)} y2={Y(t)} stroke={VIZ.grid} strokeWidth={1} />
            <text
              x={m.l - 8}
              y={Y(t) + 4}
              textAnchor="end"
              fill={VIZ.muted}
              fontSize={11}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {format(t)}
            </text>
          </g>
        ))}
        <line x1={m.l} x2={m.l} y1={m.t} y2={m.t + ph} stroke={VIZ.axis} strokeWidth={1} />

        {xTicks.map((i, j) => (
          <text
            key={i}
            x={X(i)}
            y={H - 8}
            textAnchor={j === 0 ? 'start' : j === xTicks.length - 1 ? 'end' : 'middle'}
            fill={VIZ.muted}
            fontSize={11}
          >
            {labels[i]}
          </text>
        ))}

        {/* event markers — rule + flag + banded label, never a series colour */}
        {evs.map((e) => (
          <g key={`${e.index}-${e.label}`}>
            <line
              x1={e.x}
              x2={e.x}
              y1={m.t - 6}
              y2={m.t + ph}
              stroke={e.dotted ? VIZ.muted : VIZ.axis}
              strokeWidth={1}
              strokeDasharray={e.dotted ? '2 3' : undefined}
            />
            <path d={`M${e.x},${m.t - 6}l-4,-5h8z`} fill={VIZ.axis} />
            <text x={e.lx} y={m.t - 14} fill={VIZ.muted} fontSize={11}>
              {e.label}
            </text>
            {e.lx !== e.x && (
              <line
                x1={e.lx + 2}
                x2={e.x}
                y1={m.t - 10}
                y2={m.t - 7}
                stroke={VIZ.axis}
                strokeWidth={1}
                opacity={0.6}
              />
            )}
            <rect
              x={e.x - 9}
              y={m.t - BAND}
              width={18}
              height={ph + BAND}
              fill="transparent"
              style={{ cursor: 'help' }}
              onPointerMove={(ev) => {
                ev.stopPropagation();
                tip.show(ev, [labels[e.index], e.label]);
              }}
              onPointerLeave={tip.hide}
            />
          </g>
        ))}

        {area &&
          !stacked &&
          series.map((s) => (
            <path key={`area-${s.key}`} d={areaOf(s.points)} fill={tint(s.color, 0.88)} stroke="none" />
          ))}

        {stacked &&
          series.map((s, j) => (
            <path key={`band-${s.key}`} d={bandOf(j)} fill={tint(s.color, 0.55)} stroke="none" />
          ))}

        {series.map((s, j) => (
          <path
            key={s.key}
            d={pathOf(drawn[j])}
            fill="none"
            stroke={s.color}
            strokeWidth={s.width ?? (stacked ? 1.5 : 2.5)}
            strokeDasharray={s.dashed ? '6 4' : undefined}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}

        {/* end markers: >= 8px diameter with a 2px surface-colour ring */}
        {ends.map((e) => (
          <circle
            key={e.text}
            cx={e.px}
            cy={e.py}
            r={4}
            fill={e.color}
            stroke={VIZ.surface}
            strokeWidth={2}
          />
        ))}

        {/* direct end labels as pills + leader lines */}
        {ends.map((e) => {
          const w = measureText(e.text, 12, 600) + 2 * PILL_PAD_X;
          // A single series owns the chart, so its pill is ink-filled; several share
          // it, so each wears a wash of its own hue with same-hue ink.
          const bg = named ? VIZ.ink : tint(e.color, 0.86);
          const fg = named ? '#fff' : shade(e.color, 0.35);
          return (
            <g key={`lab-${e.text}`}>
              {Math.abs(e.ly - e.py) > 2 && (
                <polyline
                  points={`${e.px + 6},${e.py} ${labelX - 6},${e.ly} ${labelX - 2},${e.ly}`}
                  fill="none"
                  stroke={e.color}
                  strokeWidth={1}
                  opacity={0.55}
                />
              )}
              <rect x={labelX} y={e.ly - PILL_H / 2} width={w} height={PILL_H} rx={8} fill={bg} />
              <text
                x={labelX + PILL_PAD_X}
                y={e.ly + 4}
                fill={fg}
                fontSize={12}
                fontWeight={600}
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {e.text}
              </text>
            </g>
          );
        })}

        {crossI != null && (
          <line
            x1={X(crossI)}
            x2={X(crossI)}
            y1={m.t}
            y2={m.t + ph}
            stroke={VIZ.axis}
            strokeWidth={1}
          />
        )}
        <rect
          x={m.l}
          y={m.t}
          width={pw}
          height={ph}
          fill="transparent"
          style={{ cursor: 'crosshair' }}
          onPointerMove={onMove}
          onPointerLeave={() => {
            setCross(null);
            tip.hide();
          }}
        />
      </svg>
      {series.length >= 2 && (
        <VizLegend kind="line" items={series.map((s) => ({ label: s.label, color: s.color }))} />
      )}
      {tip.node}
    </Box>
  );
}
